import { orderBy } from 'lodash';
import store2 from 'store2';
import { getSelectedStateCgiURL } from '../../common/cgi/Cgi';
import { getBaseUrl } from '../../common/urlBuilder';

const byoStore = store2.namespace('stp.byo');
const byo2_0Store = store2.namespace('byo');

export const SAVED_BUILD = 'saved.build';
export const CONFIG = 'config';
export const TRIM_FILTERS = 'trim.filters';

// Quantum RAQ form use local storage key, byo.<country>.<brand>.<carline>.<year>.<bodyStyle>.originUrl, to retrieve BYO URL.
// To make sure the backward compatible, 3.0 needs to write to same key.
export const setOriginUrlAs20 = (modelInfo, url) => {
  byo2_0Store.set(buildFullKey(modelInfo, 'originUrl'), url, true);
};

const buildModelKey = (model) => {
  const { country, brand, year, carline, bodyStyle } = model;
  return `${country}.${brand}.${carline}.${year}.${bodyStyle}`;
};

const buildFullKey = (model, key) => `${buildModelKey(model)}.${key}`;

const buildBodyTypeKey = (model, bodyType, key) =>
  `${buildModelKey(model)}.${bodyType}.${key}`;

const getStoreByBrandAndCountry = (brand, country) => {
  return store2.namespace(`stp.byo.${country}.${brand}`);
};

export const remove = (modelInfo, key) => {
  byoStore.remove(buildFullKey(modelInfo, key));
};

export const getByModelAndKey = (modelInfo, key) => {
  return byoStore.get(buildFullKey(modelInfo, key));
};

export const setByModelAndKey = (modelInfo, key, data) => {
  return byoStore.set(buildFullKey(modelInfo, key), data, true);
};

export const setByBodyTypeAndKey = (modelInfo, bodyType, key, data) => {
  return byoStore.set(buildBodyTypeKey(modelInfo, bodyType, key), data, true);
};

export const getByBodyTypeAndKey = (modelInfo, bodyType, key) => {
  return byoStore.get(buildBodyTypeKey(modelInfo, bodyType, key));
};

export const clearAllByModel = (model) => {
  const key = buildModelKey(model);
  const modelStore = byoStore.namespace(key);
  // modelStore.clear() is not clearing all subkeys out, iterating over instead
  modelStore.keys().forEach((key) => {
    modelStore.remove(key);
  });
};

export const getAllSavedBuilds = (brand, country) => {
  const allStorages = getStoreByBrandAndCountry(brand, country).getAll();

  const allSavedBuilds = Object.keys(allStorages)
    .filter((key) => key.includes(SAVED_BUILD))
    .map((key) => {
      const savedBuild = allStorages[key];
      const { style } = savedBuild;

      if (style) {
        const styleArray = style.split('/');
        styleArray.shift();
        savedBuild.styleWithoutLang = styleArray.join('/');
      }

      return {
        key: key,
        ...savedBuild,
      };
    });

  return orderBy(allSavedBuilds, 'savedDate', 'desc');
};

export const removeSavedBuild = (modelInfo, key) => {
  const { country, brand } = modelInfo;
  byoStore.remove(`${country}.${brand}.${key}`);
};

export const saveSavedBuild = (appResources, config) => {
  const {
    country,
    brand,
    carline,
    year,
    bodyStyle,
    bodyStyleTitle,
    exteriorAngles,
    interiorAngles,
    urls: { cgiUrl },
    evMode,
  } = appResources;

  const { style, styleId, selections } = config;

  const formattedConfig = config.bodyType.formattedConfig;

  const savedBuildWithSameSS = getAllSavedBuilds(brand, country).filter(
    (build) => config.ss === build.ss
  );

  savedBuildWithSameSS.forEach((savedBuild) => {
    removeSavedBuild(appResources, savedBuild.key);
  });

  const imageConfig = {
    year,
    cgiUrl,
    selections,
    style,
    angles: { exteriorAngles, interiorAngles },
    imageAngle: 'default',
    background: 'ffffff',
    size: 'gmds1',
    isInterior: false,
  };

  const key = `${SAVED_BUILD}.${Date.now()}`;

  const today = new Date();

  const configToSave = {
    savedDate: today,
    expiry: new Date().setDate(today.getDate() + 30),
    ss: config.ss,
    year,
    brand,
    carline,
    bodyStyle,
    bodyStyleTitle,
    formattedConfig,
    image: getSelectedStateCgiURL(imageConfig),
    styleId,
    driveType: config.driveType.id,
    trim: config.style.trimName,
    styleWithoutLang: `${country}/${brand}/${carline}/${year}/${bodyStyle}/${styleId}`,
    baseUrl: getBaseUrl(),
    isEvMode: evMode,
  };

  setByModelAndKey(appResources, key, configToSave);
};

export const getSavedBuildCount = (brand, country) => {
  const storeByBrandAndCountry = getStoreByBrandAndCountry(brand, country);

  return Object.keys(storeByBrandAndCountry.getAll()).filter((key) =>
    key.includes(SAVED_BUILD)
  ).length;
};

export const removeExpiredSavedBuilds = (brand, country) => {
  const now = new Date();
  const expiredBuilds = getAllSavedBuilds(brand, country).filter((build) => {
    return now > new Date(build.expiry);
  });

  expiredBuilds.forEach((build) =>
    byoStore.remove(`${country}.${brand}.${build.key}`)
  );
};

export const saveTrimFilters = (modelInfo, bodyType, filters) => {
  const today = new Date();

  const data = {
    ...filters,
    savedDate: today,
    expiry: new Date().setDate(today.getDate() + 30),
  };

  setByBodyTypeAndKey(modelInfo, bodyType, TRIM_FILTERS, data);
};

export const getTrimFilters = (modelInfo, bodyType) => {
  const now = new Date();
  const filters = getByBodyTypeAndKey(modelInfo, bodyType, TRIM_FILTERS);

  if (filters && now > new Date(filters.expiry)) {
    setByBodyTypeAndKey(modelInfo, bodyType, TRIM_FILTERS, '');
    return '';
  }

  return filters;
};
